export interface AudioRecorderOptions {
  maxDuration?: number; // in seconds, default 25 minutes (Whisper limit)
  sampleRate?: number;
  channels?: number;
}

export interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  error?: string;
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startTime: number = 0;
  private durationInterval: NodeJS.Timeout | null = null;
  
  private options: Required<AudioRecorderOptions>;
  private onStateChange?: (state: RecordingState) => void;

  constructor(
    options: AudioRecorderOptions = {},
    onStateChange?: (state: RecordingState) => void
  ) {
    this.options = {
      maxDuration: options.maxDuration || 25 * 60, // 25 minutes (Whisper limit)
      sampleRate: options.sampleRate || 44100,
      channels: options.channels || 1
    };
    this.onStateChange = onStateChange;
  }

  async startRecording(): Promise<void> {
    try {
      // Request microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: this.options.sampleRate,
          channelCount: this.options.channels,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      // Create MediaRecorder with optimal settings for Whisper
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        audioBitsPerSecond: 128000 // Good quality for speech
      });

      this.audioChunks = [];
      this.startTime = Date.now();

      // Set up event handlers
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.stopDurationTracking();
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        this.updateState({
          isRecording: false,
          isPaused: false,
          duration: 0,
          error: 'Aufnahmefehler aufgetreten'
        });
      };

      // Start recording
      this.mediaRecorder.start(1000); // Collect data every second
      this.startDurationTracking();

      this.updateState({
        isRecording: true,
        isPaused: false,
        duration: 0
      });

    } catch (error) {
      console.error('Failed to start recording:', error);
      let errorMessage = 'Mikrofon-Zugriff fehlgeschlagen';
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Mikrofon-Berechtigung verweigert';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'Kein Mikrofon gefunden';
        }
      }

      this.updateState({
        isRecording: false,
        isPaused: false,
        duration: 0,
        error: errorMessage
      });
      throw error;
    }
  }

  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('Keine aktive Aufnahme'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        try {
          // Create final audio blob
          const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
          const audioBlob = new Blob(this.audioChunks, { type: mimeType });
          
          // Clean up
          this.cleanup();
          
          this.updateState({
            isRecording: false,
            isPaused: false,
            duration: Math.floor((Date.now() - this.startTime) / 1000)
          });

          resolve(audioBlob);
        } catch (error) {
          reject(error);
        }
      };

      this.mediaRecorder.stop();
    });
  }

  cancelRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    this.cleanup();
    this.updateState({
      isRecording: false,
      isPaused: false,
      duration: 0
    });
  }

  private startDurationTracking(): void {
    this.durationInterval = setInterval(() => {
      const duration = Math.floor((Date.now() - this.startTime) / 1000);
      
      // Auto-stop at max duration
      if (duration >= this.options.maxDuration) {
        this.stopRecording().catch(console.error);
        return;
      }

      this.updateState({
        isRecording: true,
        isPaused: false,
        duration
      });
    }, 1000);
  }

  private stopDurationTracking(): void {
    if (this.durationInterval) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private cleanup(): void {
    this.stopDurationTracking();
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  private getSupportedMimeType(): string {
    // Prefer formats that work well with Whisper
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return ''; // Let browser choose
  }

  private updateState(state: RecordingState): void {
    if (this.onStateChange) {
      this.onStateChange(state);
    }
  }

  // Convert audio blob to format suitable for Whisper API
  static async convertForWhisper(audioBlob: Blob): Promise<File> {
    // Create a File object with proper name and type for Whisper API
    const timestamp = Date.now();
    const fileName = `recording_${timestamp}.webm`;
    
    return new File([audioBlob], fileName, {
      type: audioBlob.type || 'audio/webm'
    });
  }

  // Check if browser supports audio recording
  static isSupported(): boolean {
    return !!(
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      window.MediaRecorder
    );
  }

  pauseRecording(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'recording') {
      console.warn('Cannot pause: no active recording');
      return;
    }

    try {
      this.mediaRecorder.pause();
      this.stopDurationTracking();
      
      this.updateState({
        isRecording: true,
        isPaused: true,
        duration: Math.floor((Date.now() - this.startTime) / 1000)
      });
    } catch (error) {
      console.error('Failed to pause recording:', error);
      this.updateState({
        isRecording: false,
        isPaused: false,
        duration: Math.floor((Date.now() - this.startTime) / 1000),
        error: 'Fehler beim Pausieren der Aufnahme'
      });
    }
  }

  resumeRecording(): void {
    if (!this.mediaRecorder || this.mediaRecorder.state !== 'paused') {
      console.warn('Cannot resume: recording is not paused');
      return;
    }

    try {
      this.mediaRecorder.resume();
      this.startDurationTracking();
      
      this.updateState({
        isRecording: true,
        isPaused: false,
        duration: Math.floor((Date.now() - this.startTime) / 1000)
      });
    } catch (error) {
      console.error('Failed to resume recording:', error);
      this.updateState({
        isRecording: false,
        isPaused: false,
        duration: Math.floor((Date.now() - this.startTime) / 1000),
        error: 'Fehler beim Fortsetzen der Aufnahme'
      });
    }
  }
} 